"use client";

import { useEffect, useState } from "react";

import { usePathname } from "next/navigation";

import {
  GAM_BASE_ENTRY_MOBILE_AD_UNIT,
  GAM_BASE_ENTRY_MOBILE_ENABLED,
  GAM_BASE_ENTRY_MOBILE_MIN_HEIGHT,
  GAM_BASE_ENTRY_MOBILE_SIZES,
  GAM_BASE_NAV_TOP_MOBILE_AD_UNIT,
  GAM_BASE_NAV_TOP_MOBILE_ENABLED,
  GAM_BASE_NAV_TOP_MOBILE_MIN_HEIGHT,
  GAM_BASE_NAV_TOP_MOBILE_SIZES,
} from "@/components/ads/config";
import GamDisplaySlot, {
  type GamDisplaySlotRenderInfo,
} from "@/components/ads/GamDisplaySlot";

import styles from "./BaseArticle.module.css";

type TargetingValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>;

type EntryPlacement = "directLandingAfterRating" | "internalNavigationBeforeMedia";

type Props = {
  variant: "entryInline" | "navigationTop";
  placement?: EntryPlacement;
  targeting?: Record<string, TargetingValue | null | undefined>;
};

const MOBILE_QUERY = "(max-width: 767px)";
const SESSION_ENTRY_PATH_KEY = "bfc_session_entry_path";
const SESSION_ROUTE_VISITS_KEY = "bfc_session_route_visits";
const SESSION_CURRENT_PATH_KEY = "bfc_current_path";
const BASE_ENTRY_PREMIUM_SESSION_KEY = "bfc_base_entry_premium_session";

function getIsBasePath(pathname: string): boolean {
  return pathname.startsWith("/bases/");
}

function rememberBaseEntryPremium(info: GamDisplaySlotRenderInfo) {
  if (info.id !== "base-entry-mobile") return;

  const value = info.filled && info.isPremiumDemand ? "1" : "0";

  try {
    window.sessionStorage.setItem(BASE_ENTRY_PREMIUM_SESSION_KEY, value);
  } catch {}

  document.documentElement.toggleAttribute(
    "data-bfc-base-entry-premium",
    value === "1"
  );
}

export default function BaseMobileVisitAwareAd({
  variant,
  placement = "directLandingAfterRating",
  targeting,
}: Props) {
  const pathname = usePathname() ?? "/";
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);

    const update = () => {
      if (!mediaQuery.matches) {
        setShouldRender(false);
        return;
      }

      const currentPageIsBase = getIsBasePath(pathname);

      if (variant !== "entryInline" || !currentPageIsBase) {
        setShouldRender(false);
        return;
      }

      // Direct landings from search/social should see the base image first.
      // During client-side internal navigation, the same premium unit can move
      // above the image because the visitor already chose to open a base.
      const routeVisits = Number(
        window.sessionStorage.getItem(SESSION_ROUTE_VISITS_KEY) ?? "0"
      );
      const entryPath =
        window.sessionStorage.getItem(SESSION_ENTRY_PATH_KEY) ?? pathname;
      const trackedCurrentPath = window.sessionStorage.getItem(
        SESSION_CURRENT_PATH_KEY
      );
      const isInternalBaseNavigation =
        (routeVisits > 0 && entryPath !== pathname) ||
        Boolean(trackedCurrentPath && trackedCurrentPath !== pathname);

      setShouldRender(
        placement === "internalNavigationBeforeMedia"
          ? isInternalBaseNavigation
          : !isInternalBaseNavigation
      );
    };

    update();
    mediaQuery.addEventListener("change", update);

    return () => {
      mediaQuery.removeEventListener("change", update);
    };
  }, [pathname, placement, variant]);

  if (!shouldRender) return null;

  if (variant === "entryInline") {
    if (!GAM_BASE_ENTRY_MOBILE_ENABLED) return null;

    const isInternalPlacement = placement === "internalNavigationBeforeMedia";
    const position = isInternalPlacement
      ? "above_image_switcher_after_internal_navigation"
      : "after_base_image_switcher_and_rating";
    const placementName = isInternalPlacement
      ? "premium_mobile_internal_above_image"
      : "premium_mobile_landing_after_rating";
    const placementDetail = isInternalPlacement
      ? "base_entry_mobile_internal_before_image_switcher"
      : "base_entry_mobile_landing_after_rating_above_fold";

    return (
      <div
        className={`${styles.displayAdWrap} ${styles.premiumEntryAdWrap}`}
        data-base-mobile-ad="entry-inline"
        data-base-mobile-entry-placement={placement}
        data-base-mobile-ad-role="primary-premium"
      >
        <div className={`${styles.adBox} ${styles.premiumEntryAdBox}`}>
          <div className={styles.adHeader}>
            <span className={styles.sponsored}></span>
          </div>

          <GamDisplaySlot
            id="base-entry-mobile"
            adUnit={GAM_BASE_ENTRY_MOBILE_AD_UNIT}
            sizes={[...GAM_BASE_ENTRY_MOBILE_SIZES]}
            minHeight={GAM_BASE_ENTRY_MOBILE_MIN_HEIGHT}
            onRenderEndedAction={rememberBaseEntryPremium}
            targeting={{
              page_type: "base",
              device: "mobile",
              position,
              placement: placementName,
              placement_detail: placementDetail,
              placement_context: isInternalPlacement ? "internal_navigation" : "direct_landing",
              placement_group: "premium_mobile",
              inventory_tier: "premium",
              bfc_ad_mode: "primary_premium",
              native_candidate: "1",
              demand_formats: ["native", "fluid", "display"],
              ...targeting,
            }}
          />
        </div>
      </div>
    );
  }

  if (!GAM_BASE_NAV_TOP_MOBILE_ENABLED) return null;

  return (
    <div className={styles.displayAdWrap} data-base-mobile-ad="navigation-top">
      <div className={styles.adBox}>
        <div className={styles.adHeader}>
          <span className={styles.sponsored}>Sponsored</span>
        </div>

        <GamDisplaySlot
          id="base-nav-top-mobile"
          adUnit={GAM_BASE_NAV_TOP_MOBILE_AD_UNIT}
          sizes={[...GAM_BASE_NAV_TOP_MOBILE_SIZES]}
          minHeight={GAM_BASE_NAV_TOP_MOBILE_MIN_HEIGHT}
          targeting={{
            page_type: "base",
            device: "mobile",
            position: "above_image_switcher_after_internal_navigation",
            placement: "base_nav_top_mobile_above_image_switcher",
            ...targeting,
          }}
        />
      </div>
    </div>
  );
}
